// frontend/src/pages/StockEvents.js
import React, { useState, useEffect } from "react";
import api from "../api";

const TYPE_CONFIG = {
  add: {
    label: "Stock Added",
    color: "bg-green-100 text-green-800 border-green-200",
  },
  stocktake: {
    label: "Stock Take",
    color: "bg-blue-100 text-blue-800 border-blue-200",
  },
  waste: {
    label: "Waste / Loss",
    color: "bg-red-100 text-red-800 border-red-200",
  },
};

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || {
    label: type,
    color: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <span
      className={
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border " +
        cfg.color
      }
    >
      {cfg.label}
    </span>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return (
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
}

const PER_PAGE = 25;

export default function StockEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 500 });
    if (typeFilter) params.set("type", typeFilter);
    api
      .get("/api/stock-audit?" + params.toString())
      .then((r) => {
        setEvents(r.data);
        setError("");
      })
      .catch(() => setError("Failed to load stock events"))
      .finally(() => setLoading(false));
  }, [typeFilter]);

  const filtered = events.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.product_name || "").toLowerCase().includes(q) ||
      (e.manager_name || "").toLowerCase().includes(q) ||
      (e.reason || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleTypeChange = (val) => {
    setTypeFilter(val);
    setPage(1);
  };
  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };
  const clearFilters = () => {
    setTypeFilter("");
    setSearch("");
    setPage(1);
  };

  const hasFilters = typeFilter || search;

  return (
    <div>
      <div className="pb-5 mb-6 border-b border-gray-200">
        <div className="text-xl sm:text-2xl font-bold text-gray-900">
          Stock Events Log
        </div>
        <div className="text-sm text-gray-400 mt-0.5">
          Full history of inventory changes: stock takes, waste, and restocks
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
            type="text"
            placeholder="Search product, manager, reason..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <select
            className="sm:w-52 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white"
            value={typeFilter}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="">All event types</option>
            <option value="add">Stock Added</option>
            <option value="stocktake">Stock Take</option>
            <option value="waste">Waste / Loss</option>
          </select>
          {hasFilters && (
            <button
              className="text-sm text-blue-600 hover:underline px-2"
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
        {!loading && (
          <div className="text-xs text-gray-400 mt-2">
            {filtered.length === 0
              ? "No events match your filters"
              : "Showing " +
                filtered.length +
                " event" +
                (filtered.length !== 1 ? "s" : "") +
                (hasFilters ? " (filtered)" : "")}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            Loading events...
          </div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-2xl mb-3 font-mono">[ ]</div>
            <div className="font-medium text-sm">No stock events found</div>
            <div className="text-xs mt-1">
              {hasFilters
                ? "Try clearing your filters"
                : "Events are recorded automatically as stock changes happen"}
            </div>
          </div>
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Date &amp; Time
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Product
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Change
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Before
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      After
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      By
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Reason / Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((e, i) => {
                    const isPos = e.qty_change > 0;
                    const isZero = e.qty_change === 0;
                    return (
                      <tr
                        key={e.id}
                        className={
                          "border-b border-gray-50 hover:bg-blue-50/30 transition-colors " +
                          (i % 2 === 0 ? "" : "bg-gray-50/40")
                        }
                      >
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {fmtDate(e.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <TypeBadge type={e.type} />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {e.product_name || (
                            <span className="text-gray-400 italic">
                              Deleted product
                            </span>
                          )}
                        </td>
                        <td
                          className={
                            "px-4 py-3 text-right font-bold " +
                            (isZero
                              ? "text-gray-500"
                              : isPos
                                ? "text-green-600"
                                : "text-red-600")
                          }
                        >
                          {isPos ? "+" : ""}
                          {e.qty_change}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500">
                          {e.qty_before}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">
                          {e.qty_after}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {e.manager_name || "-"}
                        </td>
                        <td
                          className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate"
                          title={e.reason || ""}
                        >
                          {e.reason || (
                            <span className="text-gray-300 italic">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="sm:hidden flex flex-col divide-y divide-gray-100">
              {paginated.map((e) => {
                const isPos = e.qty_change > 0;
                const isZero = e.qty_change === 0;
                return (
                  <div key={e.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <TypeBadge type={e.type} />
                      <span
                        className={
                          "font-bold text-base " +
                          (isZero
                            ? "text-gray-500"
                            : isPos
                              ? "text-green-600"
                              : "text-red-600")
                        }
                      >
                        {isPos ? "+" : ""}
                        {e.qty_change}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-900 mb-1">
                      {e.product_name || "Deleted product"}
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500 mb-1">
                      <span>
                        Before: <b>{e.qty_before}</b>
                      </span>
                      <span>
                        After: <b>{e.qty_after}</b>
                      </span>
                      {e.manager_name && (
                        <span>
                          By: <b>{e.manager_name}</b>
                        </span>
                      )}
                    </div>
                    {e.reason && (
                      <div className="text-xs text-gray-400 italic">
                        {e.reason}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {fmtDate(e.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 hover:bg-white"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 hover:bg-white"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
